<script setup lang="ts">
import { Property, propertyIcon, propertyName } from "~/classes/entity";
import { InterfaceSelection } from "~/classes/entity/InterfaceSelection"
import { InterfaceFile } from "~/classes/file/InterfaceFile"

const props = defineProps({
  facet: null
})

const { getFileProperties } = useFileSystem()
const { addEntityEventListener, removeEntityEventListener } = useEvents()
const { searchFacetTypeName } = useInstantMenu()
const file = ref<InterfaceFile>()
let fileId: string | undefined
if ("nearFile" in props.facet.facetType)
  fileId = props.facet.facetType.nearFile
else if ("fileIn" in props.facet.facetType)
  fileId = props.facet.facetType.fileIn
else if ("fileOnlyIn" in props.facet.facetType)
  fileId = props.facet.facetType.fileOnlyIn
if (isDefined(fileId))
  getFileProperties(fileId, InterfaceSelection.minimumPathSelection().inner(), InterfaceSelection.minimumPathSelection().inner())

const onFileProperties = (event: FileEvent) => {
  file.value = file.value ? file.value.mergeWithNewer(new InterfaceFile(event)) : new InterfaceFile(event)
}

onMounted(() => {
  if (isDefined(fileId))
    addEntityEventListener("fileProperties", fileId, onFileProperties)
})

onUnmounted(() => {
  if (isDefined(fileId))
    removeEntityEventListener("fileProperties", fileId, onFileProperties)
})
</script>

<template>
  <div class="facet">
    <template v-if="'exists' in facet.facetType">
      <div class="facet-property-filter">
        <icon-property
            size="xs"
            :type="propertyIcon(facet.propertyId as Property)"
            class="property-icon"
          />
        {{ propertyName(facet.propertyId as Property) }}
        {{ facet.facetType.exists ? 'Exists' : 'Does not exist' }}
      </div>
    </template>
    <template v-else-if="'equals' in facet.facetType">
      <div class="facet-property-filter">
        <icon-property
            size="xs"
            :type="propertyIcon(facet.propertyId as Property)"
            class="property-icon"
          />
        {{ propertyName(facet.propertyId as Property) }}
        =
        "{{ facet.facetType.equals }}"
      </div>
    </template>
    <template v-else-if="'notEquals' in facet.facetType">
      <div class="facet-property-filter">
        <icon-property
            size="xs"
            :type="propertyIcon(facet.propertyId as Property)"
            class="property-icon"
          />
        {{ propertyName(facet.propertyId as Property) }}
        ≠
        "{{ facet.facetType.notEquals }}"
      </div>
    </template>
    <template v-else-if="'startsWith' in facet.facetType && facet.propertyId !== Property.FILE_NAME">
      <div class="facet-property-filter">
        <icon-property
            size="xs"
            :type="propertyIcon(facet.propertyId as Property)"
            class="property-icon"
          />
        {{ propertyName(facet.propertyId as Property) }}
        Starts with
        "{{ facet.facetType.startsWith }}"
      </div>
    </template>
    <template v-else-if="'contains' in facet.facetType && facet.propertyId !== Property.FILE_NAME">
      <div class="facet-property-filter">
        <icon-property
            size="xs"
            :type="propertyIcon(facet.propertyId as Property)"
            class="property-icon"
          />
        {{ propertyName(facet.propertyId as Property) }}
        Contains
        "{{ facet.facetType.contains }}"
      </div>
    </template>
    <template v-else-if="'startsWith' in facet.facetType">
      <div class="facet-starts-with">
        {{ facet.facetType.startsWith }}
      </div>
    </template>
    <template v-else-if="'contains' in facet.facetType">
      <div class="facet-contains">
        {{ facet.facetType.contains }}
      </div>
    </template>
    <template v-else-if="'nearColor' in facet.facetType">
      <div class="facet-color-picker">
        <div>Similar to</div>
        <div :style="{ backgroundColor: facet.facetType.nearColor }" class="color" />
        <div class="code">
          {{ facet.facetType.nearColor.replace("#", "") }}
        </div>
      </div>
    </template>
    <template v-else-if="'nearDocLikeText' in facet.facetType">
      <div class="facet-theme">
        <div>Theme is</div>
        <div>"{{ facet.facetType.nearDocLikeText }}"</div>
      </div>
    </template>
    <template v-else-if="'nearImageText' in facet.facetType">
      <div class="facet-theme">
        <div>Theme is</div>
        <div>"{{ facet.facetType.nearImageText }}"</div>
      </div>
    </template>
    <template v-else-if="'nearVideoText' in facet.facetType">
      <div class="facet-theme">
        <div>Theme is</div>
        <div>"{{ facet.facetType.nearVideoText }}"</div>
      </div>
    </template>
    <template v-else-if="'nearFile' in facet.facetType">
      <div class="facet-file">
        <div>Similar to</div>
        <file-inline
          v-if="file"
          :file="file"
          class="near-file-facet-file"
        />
      </div>
    </template>
    <template v-else-if="'fileIn' in facet.facetType">
      <div class="facet-file">
        <div>In</div>
        <file-inline v-if="file" :file="file" />
      </div>
    </template>
    <template v-else-if="'fileOnlyIn' in facet.facetType">
      <div class="facet-file">
        <div>Only In</div>
        <file-inline v-if="file" :file="file" />
      </div>
    </template>
    <template v-else>
      ?
    </template>
  </div>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'

.facet
  @apply flex items-center gap-[4px]

.facet-property-filter
  @apply flex items-center gap-[4px] py-1 px-1.5 rounded-[6px]
  @apply bg-$global-blue-accent
  @apply t-property-sm
  .property-icon
    @apply text-$global-blue-primary

.facet-color-picker
  @apply flex items-center gap-[4px] py-1 px-1.5 rounded-[6px]
  @apply bg-[#{$menu-search-bg-item-facet-section-hovered}]
  @apply t-property-sm
  .color
    @apply w-[9px] h-[9px] rounded-full
  .code
    @apply font-mono uppercase

.facet-theme
  @apply flex items-center gap-[4px] py-1 px-1.5 rounded-[6px]
  @apply bg-[#{$menu-search-bg-item-facet-section-hovered}]
  @apply t-property-sm

.facet-file
  @apply flex items-center gap-[4px] py-1 px-1.5 rounded-[6px]
  @apply bg-[#{$menu-search-bg-item-facet-section-hovered}]
  @apply t-property-sm

.near-file-facet-file
  @apply max-w-[250px] // TODO: this seems to be required do to some CSS reasons I can't understand
</style>
